import { useCallback, useEffect, useMemo, useState } from "react";
import type { TerraformCommandKind } from "../../shared/terraform";
import {
  mergeTerraformProjection,
  terraformStateIsRunning,
  type TerraformProjection,
  type TerraformSelectionProjection,
  type TerraformStateProjection,
} from "../../shared/terraform-projection";
import { loadTerraformSelection, loadTerraformState, runTerraformCommand } from "../lib/terraform";
import { startVisibilityPolling } from "../lib/visibility-polling";

/**
 * The terraform panel's data, in the order it can be had.
 *
 * The selection arrives from a cache and draws the panel; Atlantis's state
 * arrives second and fills the cells in. Fetching them together is what used
 * to make opening the panel feel like loading a different application: the
 * slowest of five authorities decided when anything appeared, and four of them
 * were only needed to colour cells the reader had not looked at yet.
 *
 * Both layers are cached in a module-level map, so closing the panel and
 * reopening it — or opening it on a pull request already looked at this
 * session — paints from memory.
 */

interface CachedLayers {
  selection?: TerraformSelectionProjection;
  state?: TerraformStateProjection;
}

/**
 * Answers kept for the session, keyed by pull request.
 *
 * Outside React state on purpose: the point is to survive the panel being
 * unmounted, which is exactly what closing it does.
 */
const cache = new Map<string, CachedLayers>();

const cacheKey = (repository: string, pullNumber: number): string => `${repository}#${pullNumber}`;

/**
 * Forget everything cached.
 *
 * Here for the tests, which would otherwise see one case's answer in the next
 * one — the cache outliving a mount is the whole feature, so a test cannot
 * clear it by unmounting.
 */
export function clearTerraformPanelCache(): void {
  cache.clear();
}

export interface TerraformPanelData {
  /** The merged projection, or undefined until the selection has arrived. */
  projection?: TerraformProjection;
  /** Why the selection could not be loaded. The state layer failing is survivable. */
  error?: unknown;
  busy: boolean;
  posted?: { command: string; url: string };
  commandError?: string;
  dismissPosted: () => void;
  dismissCommandError: () => void;
  run: (kind: TerraformCommandKind, project?: string) => Promise<void>;
}

/**
 * Warm the selection without rendering anything.
 *
 * Called when a review page knows it is looking at an infrastructure pull
 * request, so the panel is already loaded by the time anyone opens it. Failures
 * are swallowed: this is an optimisation, and the panel asks again properly.
 */
export function prefetchTerraformSelection(repository: string, pullNumber: number): void {
  const key = cacheKey(repository, pullNumber);
  if (cache.get(key)?.selection) return;
  void loadTerraformSelection(repository, pullNumber)
    .then((selection) => {
      cache.set(key, { ...cache.get(key), selection });
    })
    .catch(() => undefined);
}

export function useTerraformPanelData(repository: string, pullNumber: number): TerraformPanelData {
  const key = cacheKey(repository, pullNumber);
  const [selection, setSelection] = useState<TerraformSelectionProjection | undefined>(() => cache.get(key)?.selection);
  const [state, setState] = useState<TerraformStateProjection | undefined>(() => cache.get(key)?.state);
  const [error, setError] = useState<unknown>();
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState<{ command: string; url: string }>();
  const [commandError, setCommandError] = useState<string>();

  const remember = useCallback((layers: CachedLayers) => {
    cache.set(key, { ...cache.get(key), ...layers });
  }, [key]);

  useEffect(() => {
    let cancelled = false;
    void loadTerraformSelection(repository, pullNumber)
      .then((next) => {
        if (cancelled) return;
        setSelection(next);
        remember({ selection: next });
      })
      .catch((reason: unknown) => {
        // A cached selection is still worth showing; it is this commit's
        // answer, and the only thing a failure changes is that it is not
        // freshly confirmed.
        if (!cancelled && !cache.get(key)?.selection) setError(reason);
      });
    return () => { cancelled = true; };
  }, [key, pullNumber, remember, repository]);

  const projection = useMemo(
    () => (selection ? mergeTerraformProjection(selection, state) : undefined),
    [selection, state],
  );

  /**
   * Atlantis answers a command by editing the pull request, so the state layer
   * is always a moment behind. Polling only while the tab is visible keeps that
   * honest without hammering GitHub from a forgotten background tab, and a run
   * in flight is the one time the shorter interval is worth paying for.
   */
  const awaitingAtlantis = projection?.projects.some((row) => terraformStateIsRunning(row.state)) ?? false;
  useEffect(() => startVisibilityPolling(async () => {
    const next = await loadTerraformState(repository, pullNumber);
    setState(next);
    remember({ state: next });
  }, { activeMs: awaitingAtlantis ? 6_000 : 20_000 }), [awaitingAtlantis, pullNumber, remember, repository]);

  const run = useCallback(async (kind: TerraformCommandKind, project?: string) => {
    if (!selection) return;
    setBusy(true);
    setCommandError(undefined);
    setPosted(undefined);
    try {
      const result = await runTerraformCommand({
        repository,
        pullNumber,
        headSha: selection.headSha,
        kind,
        project,
      });
      setPosted({ command: result.command, url: result.comment.url });
      const next = await loadTerraformState(repository, pullNumber);
      setState(next);
      remember({ state: next });
    } catch (reason) {
      setCommandError(reason instanceof Error ? reason.message : "CHEW could not leave that command.");
    } finally {
      setBusy(false);
    }
  }, [pullNumber, remember, repository, selection]);

  return {
    projection,
    error,
    busy,
    posted,
    commandError,
    dismissPosted: useCallback(() => setPosted(undefined), []),
    dismissCommandError: useCallback(() => setCommandError(undefined), []),
    run,
  };
}
